import { useEffect } from "react";
import type { MutationFunction } from "react-query";
import { useOtpChallengeContext } from "@/react/contexts/OtpChallengeContext";
import type { ApiError } from "@circle-react/config/CustomErrors";

const OTP_HEADERS = {
  challenge: "X-OTP-Challenge",
  code: "X-OTP-Challenge-Code",
} as const;

const isOtpChallengeRequired = (error: ApiError): boolean =>
  error.response?.headers?.get(OTP_HEADERS.challenge) === "true";

const buildOtpRequest = (
  error: ApiError,
  otpCode: string,
): RequestInit & { url: string } => {
  if (!error.request) {
    throw new Error("Request information not available in error");
  }

  const request = error.request as {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string;
  };

  return {
    url: request.url,
    method: request.method,
    headers: {
      ...(request.headers || {}),
      [OTP_HEADERS.code]: otpCode,
    },
    body: request.body,
    credentials: "same-origin" as const,
  };
};

const handleOtpResponse = async (response: Response): Promise<unknown> => {
  if (response.ok) {
    return response.json();
  }

  const text = await response.text();
  const data = text ? (JSON.parse(text) as { message?: string }) : {};

  throw new Error(data.message);
};

interface UseOtpChallengeProps<TData, TVariables> {
  email?: string;
  mutationFn: MutationFunction<TData, TVariables>;
  otpType: "authentication" | "verification";
}

export const useOtpChallenge = <TData, TVariables>({
  mutationFn,
  email,
  otpType,
}: UseOtpChallengeProps<TData, TVariables>) => {
  const { runMutationWithOtp, setEmail, setOtpType } = useOtpChallengeContext();

  useEffect(() => {
    setEmail(email);
  }, [email, setEmail]);
  useEffect(() => {
    setOtpType(otpType);
  }, [otpType, setOtpType]);

  return {
    mutateAsync: async (variables: TVariables): Promise<TData> => {
      try {
        return await mutationFn(variables);
      } catch (e) {
        const error = e as ApiError;

        if (!isOtpChallengeRequired(error)) {
          throw e;
        }

        return await runMutationWithOtp(async otpCode => {
          const request = buildOtpRequest(error, otpCode);
          const response = await fetch(request.url, request);
          return handleOtpResponse(response) as TData;
        });
      }
    },
  };
};
